import { useState } from 'react';
import { ArrowRight, TrendingUp, Mail, Users, MousePointer } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/matthew-trottstudios';
const WORD_ANIMATION_DELAY_MS = 30;

// Data configurations
const STATS_DATA = [
  { id: 'revenue', value: '+127%', label: 'Revenue Growth', icon: TrendingUp },
  { id: 'email', value: '48%', label: 'Email Open Rate', icon: Mail },
  { id: 'projects', value: '50+', label: 'Projects Delivered', icon: Users },
];

const TAGLINE_WORDS = [
  { id: 'word-1', text: 'Copy' },
  { id: 'word-2', text: 'that' },
  { id: 'word-3', text: 'hooks,' },
  { id: 'word-4', text: 'marketing' },
  { id: 'word-5', text: 'that' },
  { id: 'word-6', text: 'closes' },
];

/**
 * HeroBackground - Animated background elements
 */
const HeroBackground = () => (
  <>
    <div className="absolute inset-0 grid-pattern animate-pulse-slow" />
    <div className="absolute top-20 right-10 w-96 h-96 bg-[#00D4FF]/15 rounded-full blur-3xl animate-blob" />
    <div className="absolute bottom-40 left-10 w-80 h-80 bg-[#0EA5E9]/15 rounded-full blur-3xl animate-blob animation-delay-2000" />
    <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-[#00D4FF]/10 rounded-full blur-3xl animate-blob animation-delay-4000" />
    <div className="absolute top-32 right-[20%] w-3 h-3 bg-[#00D4FF] rounded-full animate-float opacity-60" />
    <div className="absolute top-48 right-[35%] w-2 h-2 bg-[#1E1E2E] rounded-full animate-float animation-delay-1000 opacity-40" />
    <div className="absolute bottom-48 left-[20%] w-4 h-4 bg-[#00D4FF]/50 rounded-full animate-float animation-delay-2000" />
  </>
);

/**
 * HeroHeadline - Main headline "Trott Studios"
 */
const HeroHeadline = () => (
  <div className="mb-4 animate-fade-in-up">
    <h1
      className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight text-[#1E1E2E] leading-none"
      style={{ fontFamily: 'Manrope, sans-serif' }}
    >
      <span className="inline-block hover:scale-105 transition-transform duration-300 cursor-default">
        Trott
      </span>{' '}
      <span className="inline-block text-gradient hover:scale-105 transition-transform duration-300 cursor-default">
        Studios
      </span>
    </h1>
  </div>
);

/**
 * HeroTagline - Interactive tagline with hover effects
 */
const HeroTagline = ({ hoveredWord, setHoveredWord }) => (
  <div 
    data-testid="hero-headline"
    className="mb-10 animate-fade-in-up-delay-1"
  >
    <p className="text-2xl sm:text-3xl md:text-4xl font-medium text-[#1E1E2E]/80 leading-relaxed tracking-wide">
      {TAGLINE_WORDS.map((word, index) => (
        <span
          key={word.id}
          className={`inline-block mr-3 cursor-default transition-all duration-300 ${
            hoveredWord === index 
              ? 'text-[#00D4FF] scale-110 -translate-y-1' 
              : 'hover:text-[#00D4FF]'
          }`}
          onMouseEnter={() => setHoveredWord(index)}
          onMouseLeave={() => setHoveredWord(null)}
          style={{ 
            fontFamily: "'Cormorant Garamond', serif",
            transitionDelay: `${index * WORD_ANIMATION_DELAY_MS}ms`
          }}
        >
          {word.text}
        </span>
      ))}
    </p>
    <div className="mt-4 h-1 bg-gradient-to-r from-[#00D4FF] via-[#0EA5E9] to-transparent rounded-full w-48 md:w-64 animate-pulse-slow" />
  </div>
);

/**
 * HeroSubheadline - Secondary descriptive text
 */
const HeroSubheadline = () => (
  <p
    data-testid="hero-subheadline"
    className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl animate-fade-in-up-delay-1"
  >
    Email campaigns. Newsletters. Websites. Social media. 
    <span className="text-[#1E1E2E] font-medium"> Strategy to execution.</span>
  </p>
);

/**
 * HeroStats - Stats cards row
 */
const HeroStats = () => (
  <div className="flex flex-wrap gap-4 md:gap-5 mb-10 animate-fade-in-up-delay-1">
    {STATS_DATA.map((stat) => (
      <div 
        key={stat.id}
        className="group flex items-center gap-3 bg-white rounded-2xl px-5 py-4 shadow-sm border border-gray-100 hover:border-[#00D4FF]/50 hover:shadow-lg hover:shadow-[#00D4FF]/10 transition-all duration-300 cursor-pointer hover:-translate-y-1"
      >
        <div className="w-12 h-12 rounded-xl bg-[#00D4FF]/10 flex items-center justify-center group-hover:bg-[#00D4FF] group-hover:scale-110 transition-all duration-300">
          <stat.icon size={20} className="text-[#00D4FF] group-hover:text-[#1E1E2E] transition-colors" />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#1E1E2E] group-hover:text-[#00D4FF] transition-colors">{stat.value}</p>
          <p className="text-xs text-gray-500">{stat.label}</p>
        </div>
      </div>
    ))}
  </div>
);

/**
 * HeroCTAs - Call to action buttons
 */
const HeroCTAs = ({ onScrollToServices }) => (
  <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up-delay-2">
    <a
      href={CALENDLY_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="hero-cta-primary"
      className="group relative inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold bg-[#00D4FF] text-[#1E1E2E] overflow-hidden transition-all duration-300 hover:scale-105 shadow-lg shadow-[#00D4FF]/25 hover:shadow-[#00D4FF]/40"
    >
      <span className="relative z-10 flex items-center gap-2">
        Book a Call
        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
      </span>
      <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity" />
    </a>
    <a
      href="#services"
      onClick={onScrollToServices}
      data-testid="hero-cta-secondary"
      className="group relative inline-flex items-center justify-center rounded-full px-8 py-4 text-base font-semibold bg-[#1E1E2E] text-white overflow-hidden transition-all duration-300 hover:bg-[#2D2D3F]"
    >
      <span className="relative z-10 flex items-center gap-2">
        See Services
        <MousePointer size={16} className="group-hover:rotate-12 transition-transform" />
      </span>
    </a>
  </div>
);

/**
 * ScrollIndicator - Scroll down indicator
 */
const ScrollIndicator = () => (
  <div className="hidden md:flex items-center gap-2 mt-16 text-gray-400 animate-bounce-slow">
    <div className="w-6 h-10 rounded-full border-2 border-gray-300 flex items-start justify-center p-1">
      <div className="w-1.5 h-2.5 bg-[#00D4FF] rounded-full animate-scroll-down" />
    </div>
    <span className="text-sm">Scroll to explore</span>
  </div>
);

/**
 * Hero - Main hero section component
 */
export const Hero = () => {
  const [hoveredWord, setHoveredWord] = useState(null);
  
  const scrollToServices = (e) => {
    e.preventDefault();
    const element = document.querySelector('#services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 pb-32 overflow-hidden bg-[#FAFAFA]"
    >
      <HeroBackground />

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-20 relative z-10">
        <div className="max-w-5xl">
          <HeroHeadline />
          <HeroTagline hoveredWord={hoveredWord} setHoveredWord={setHoveredWord} />
          <HeroSubheadline />
          <HeroStats />
          <HeroCTAs onScrollToServices={scrollToServices} />
          <ScrollIndicator />
        </div>
      </div>
    </section>
  );
};

export default Hero;
